import { showUserAlert } from "../utils/userErrorMessage";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Header from "../Component/Header";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { supabase } from "../supabase/client";
import { deleteFirstProfileForClient, syncClientToFirstProfile } from "../utils/clientProfileSync";

const emptyForm = {
  name: "",
  hire_date: "",
  position: "",
  branch: "",
  user_id: "",
  email: "",
  phone: "",
  note: "",
  company_name: "",
};

export default function UserEditForm() {
  const navigate = useNavigate();
  const { source, id } = useParams();
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [originalClient, setOriginalClient] = useState(null);

  const company = localStorage.getItem("email") || "";
  const isClient = source === "client";
  const isProfile = source === "profiles";

  useEffect(() => {
    const loadUser = async () => {
      setLoading(true);
      try {
        if (isClient) {
          const { data, error } = await supabase
            .from("client")
            .select("id, company, name, hire_date, position, branch, user_id, email, phone, note, company_name")
            .eq("id", id)
            .eq("company", company)
            .maybeSingle();

          if (error) throw error;
          if (!data) {
            showUserAlert("사용자 정보를 찾을 수 없습니다.");
            navigate("/user-management");
            return;
          }

          setForm({
            name: data.name || "",
            hire_date: data.hire_date || "",
            position: data.position || "",
            branch: data.branch || "",
            user_id: data.user_id || "",
            email: data.email || "",
            phone: data.phone || "",
            note: data.note || "",
            company_name: data.company_name || "",
          });
          setOriginalClient(data);
          return;
        }

        if (isProfile) {
          const { data, error } = await supabase
            .from("profiles")
            .select("id, driver_name, hire_date, position, branch, user_id, email")
            .eq("id", id)
            .eq("company", company)
            .maybeSingle();

          if (error) throw error;
          if (!data) {
            showUserAlert("사용자 정보를 찾을 수 없습니다.");
            navigate("/user-management");
            return;
          }

          setForm({
            name: data.driver_name || "",
            hire_date: data.hire_date || "",
            position: data.position || "",
            branch: data.branch || "",
            user_id: data.user_id || "",
            email: data.email || "",
            phone: "",
            note: "",
            company_name: "",
          });
          return;
        }

        showUserAlert("잘못된 사용자 수정 경로입니다.");
        navigate("/user-management");
      } catch (error) {
        showUserAlert("사용자 정보를 불러오지 못했습니다: " + error.message);
      } finally {
        setLoading(false);
      }
    };

    loadUser();
  }, [company, id, isClient, isProfile, navigate]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      if (isClient) {
        const payload = {
          name: form.name,
          hire_date: form.hire_date || null,
          position: form.position,
          branch: form.branch,
          user_id: originalClient?.user_id || form.user_id,
          email: form.email,
          phone: form.phone,
          note: form.note,
          company_name: form.company_name || localStorage.getItem("companyName") || company,
        };
        const { error } = await supabase
          .from("client")
          .update(payload)
          .eq("id", id)
          .eq("company", company);

        if (error) throw error;

        await syncClientToFirstProfile(
          { ...(originalClient || {}), ...payload, company },
          {
            company,
            companyName: payload.company_name,
            previousEmail: originalClient?.email,
            previousUserId: originalClient?.user_id,
          }
        );
      } else if (isProfile) {
        const { error } = await supabase
          .from("profiles")
          .update({
            driver_name: form.name,
            hire_date: form.hire_date || null,
            position: form.position,
            branch: form.branch,
            user_id: form.user_id,
            email: form.email,
          })
          .eq("id", id)
          .eq("company", company);

        if (error) throw error;
      }

      showUserAlert("수정이 완료되었습니다.");
      navigate("/user-management");
    } catch (error) {
      showUserAlert("수정 실패: " + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!isClient) {
      showUserAlert("로그인 사용자의 기본 프로필은 이 화면에서 삭제할 수 없습니다.");
      return;
    }

    if (!window.confirm("해당 사용자를 삭제하시겠습니까?")) return;

    setSaving(true);
    try {
      if (originalClient) {
        await deleteFirstProfileForClient(originalClient, { company });
      }

      const { error } = await supabase
        .from("client")
        .delete()
        .eq("id", id)
        .eq("company", company);

      if (error) throw error;

      showUserAlert("삭제되었습니다.");
      navigate("/user-management");
    } catch (error) {
      showUserAlert("삭제 실패: " + error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <Header />
      <div className="p-4">
        <h2 className="text-lg font-semibold">사용자 수정</h2>
        <Card className="p-4">
          <CardContent className="bg-white">
            {loading ? (
              <div className="py-8 text-center text-sm text-gray-500">불러오는 중...</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block font-semibold mb-1">이름</label>
                  <Input value={form.name} onChange={(e) => handleChange("name", e.target.value)} />
                </div>

                <div>
                  <label className="block font-semibold mb-1">입사일</label>
                  <Input
                    type="date"
                    value={form.hire_date || ""}
                    onChange={(e) => handleChange("hire_date", e.target.value)}
                  />
                </div>

                <div>
                  <label className="block font-semibold mb-1">직위</label>
                  <Input value={form.position} onChange={(e) => handleChange("position", e.target.value)} />
                </div>

                <div>
                  <label className="block font-semibold mb-1">지점명</label>
                  <Input value={form.branch} onChange={(e) => handleChange("branch", e.target.value)} />
                </div>

                <div className="md:col-span-2">
                  <label className="block font-semibold mb-1">Email</label>
                  <Input type="email" value={form.email} onChange={(e) => handleChange("email", e.target.value)} />
                </div>

                <div className="md:col-span-2 flex justify-between gap-2">
                  <Button variant="outline" onClick={() => navigate(-1)} disabled={saving}>
                    이전
                  </Button>
                  <div className="flex gap-2">
                    <Button variant="destructive" onClick={handleDelete} disabled={saving}>
                      삭제
                    </Button>
                    <Button onClick={handleSubmit} disabled={saving}>
                      완료
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
